/** @jsxImportSource @emotion/react */
import { useMemo } from 'react';
import { Button } from '@mui/material';
import { ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import SectionBox from '../../components/SectionBox';
import { COLS, HIDDEN_DETAIL_COL_IDS } from '../constants';
import { useUtComparisonElecStore } from '../store/useUtComparisonElecStore';
import { ElecDiffRow, ElecRowVals, ElecSourceRow, TypeFilter } from '../types';
import { getRowVals } from '../utils/compareElec';
import ColumnFilterDropdown from './ColumnFilterDropdown';

const TYPE_FILTERS: TypeFilter[] = ['전체', '변경', '추가', '삭제', '일치'];

const LOC_FIELD: Partial<Record<keyof ElecRowVals, keyof ElecSourceRow>> = {
  bizAreaNm: 'bizAreaNm',
  bldgFloCd: 'bldgFloCd',
  istlLocNm: 'istlLocNm',
  eqpMainNm: 'eqpMainNm',
  eqpMchNm: 'eqpMchNm',
};

const matchesTypeFilter = (row: ElecDiffRow, f: TypeFilter): boolean => {
  if (f === '전체') return true;
  if (f === '변경') return row.difTpCd === 'CHG';
  if (f === '추가') return row.difTpCd === 'ADD';
  if (f === '삭제') return row.difTpCd === 'DEL';
  return row.difTpCd === 'SAME';
};

const isLocChanged = (row: ElecDiffRow, colId: keyof ElecRowVals): boolean => {
  if (!row.difTpCtn.includes('위치변경')) return false;
  const field = LOC_FIELD[colId];
  if (!field) return false;
  const b = row.before?.[field];
  const a = row.after?.[field];
  return Boolean(b && a && b !== a);
};

/** ② 변경 내역 상세 — 유형 필터 + 칼럼 표시/숨기기 + 칼럼별 필터/정렬 */
const ElecDetailTable = () => {
  const diffRows = useUtComparisonElecStore((s) => s.diffRows);
  const typeFilter = useUtComparisonElecStore((s) => s.typeFilter);
  const columnFilters = useUtComparisonElecStore((s) => s.columnFilters);
  const sort = useUtComparisonElecStore((s) => s.sort);
  const hiddenCols = useUtComparisonElecStore((s) => s.hiddenCols);
  const setTypeFilter = useUtComparisonElecStore((s) => s.setTypeFilter);
  const setColumnFilter = useUtComparisonElecStore((s) => s.setColumnFilter);
  const setSort = useUtComparisonElecStore((s) => s.setSort);
  const toggleHiddenCol = useUtComparisonElecStore((s) => s.toggleHiddenCol);
  const clearAllFilters = useUtComparisonElecStore((s) => s.clearAllFilters);

  const typeFiltered = useMemo(
    () => (diffRows ?? []).filter((r) => matchesTypeFilter(r, typeFilter)),
    [diffRows, typeFilter]
  );

  /** 특정 칼럼 필터를 제외한 나머지 필터만 적용 — 필터 드롭다운의 "선택 가능한 값" 계산용 */
  const filteredExcluding = (excludeCol?: keyof ElecRowVals) => {
    let rows = typeFiltered;
    for (const [col, vals] of Object.entries(columnFilters)) {
      if (col === excludeCol || !vals || vals.size === 0) continue;
      rows = rows.filter((r) => vals.has(String(getRowVals(r)[col as keyof ElecRowVals])));
    }
    return rows;
  };

  const filtered = useMemo(() => filteredExcluding(), [typeFiltered, columnFilters]);

  const sorted = useMemo(() => {
    if (!sort.col) return filtered;
    const col = sort.col;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((ra, rb) => {
      const va = getRowVals(ra)[col];
      const vb = getRowVals(rb)[col];
      const na = typeof va === 'number' ? va : parseFloat(String(va));
      const nb = typeof vb === 'number' ? vb : parseFloat(String(vb));
      if (!Number.isNaN(na) && !Number.isNaN(nb) && String(va) !== '' && String(vb) !== '') {
        return (na - nb) * factor;
      }
      return String(va).localeCompare(String(vb), 'ko') * factor;
    });
  }, [filtered, sort]);

  // 노출만 막는 칼럼은 사용자 토글(hiddenCols)보다 앞에서 걷어낸다 —
  // 그래야 열 표시/숨기기 바에도 뜨지 않아 다시 켤 수 없다.
  const cols = useMemo(() => COLS.filter((c) => !HIDDEN_DETAIL_COL_IDS.has(c.id)), []);
  const visibleCols = cols.filter((c) => !hiddenCols.has(c.id));

  if (!diffRows) return null;

  return (
    <SectionBox
      no="②"
      title="변경 내역 상세"
      /* 총 건수는 필터·정렬을 거친 표시 행 수, 보조 문구는 필터 전 전체 건수 */
      count={sorted.length}
      note={
        <>
          전체 <strong>{diffRows.length.toLocaleString()}</strong>건
        </>
      }
    >
      <div className="filter-bar">
        {TYPE_FILTERS.map((t) => (
          <button
            key={t}
            type="button"
            className={`filter-btn f-${t}${typeFilter === t ? ' active' : ''}`}
            onClick={() => setTypeFilter(t)}
          >
            {t}
          </button>
        ))}
        {/* 공용 버튼 규격을 쓰려고 ControlBtnGroup으로 감쌌다 — 아이콘은 className이 붙여준다 */}
        <ControlBtnGroup style={{ marginLeft: 'auto' }}>
          <Button
            css={IconButton.button}
            className="reload"
            onClick={clearAllFilters}
            disableRipple
          >
            초기화
          </Button>
        </ControlBtnGroup>
      </div>
      <div className="col-ctrl-bar">
        <span>열 표시/숨기기:</span>
        {cols.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`col-toggle${hiddenCols.has(c.id) ? ' hid' : ''}`}
            onClick={() => toggleHiddenCol(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="tbl-wrap">
        <table className="result">
          <thead>
            <tr>
              {visibleCols.map((c) => (
                <ColumnFilterDropdown
                  key={c.id}
                  label={c.label}
                  width={c.width}
                  align={c.align}
                  thClassName={
                    c.group === 'loc' ? 'th-loc' : c.group === 'elec' ? 'th-elec' : undefined
                  }
                  selected={columnFilters[c.id]}
                  sortDir={sort.col === c.id ? sort.dir : null}
                  getAllValues={() =>
                    Array.from(
                      new Set(filteredExcluding(c.id).map((r) => String(getRowVals(r)[c.id])))
                    ).sort((a, b) => a.localeCompare(b, 'ko'))
                  }
                  onApplyFilter={(values) => setColumnFilter(c.id, values)}
                  onSetSort={(dir) => setSort(c.id, dir)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length} className="empty">
                  해당 데이터가 없습니다
                </td>
              </tr>
            ) : (
              sorted.map((row) => {
                const v = getRowVals(row);
                const rowCls =
                  row.difTpCd === 'SAME'
                    ? 'r-same'
                    : row.difTpCd === 'ADD'
                    ? 'r-add'
                    : row.difTpCd === 'DEL'
                    ? 'r-del'
                    : 'r-chg';
                const lineChanged = row.difTpCtn.includes('Line변경');
                const brkChanged = row.difTpCtn.includes('차단기변경');
                const connChanged = row.difTpCtn.includes('ConnType변경');
                const badgeCls =
                  row.difTpCd === 'SAME'
                    ? 'badge-same'
                    : row.difTpCd === 'ADD'
                    ? 'badge-add'
                    : row.difTpCd === 'DEL'
                    ? 'badge-del'
                    : 'badge-chg';
                return (
                  <tr key={row.key} className={rowCls}>
                    {visibleCols.map((c) => {
                      const value = v[c.id];
                      if (c.id === 'difTpCtn') {
                        return (
                          <td key={c.id} style={{ textAlign: c.align }}>
                            <span className={`badge ${badgeCls}`} title={row.difTpCtn}>
                              {row.difTpCtn}
                            </span>
                          </td>
                        );
                      }
                      let extraCls = '';
                      if ((c.id === 'lnBf' || c.id === 'lnAf') && lineChanged) extraCls = ' red';
                      else if ((c.id === 'brkBf' || c.id === 'brkAf') && brkChanged)
                        extraCls = ' red';
                      else if (isLocChanged(row, c.id)) extraCls = ' red';
                      else if (c.id === 'connType' && connChanged) extraCls = ' conn-chg';
                      const text = value === '' || value == null ? '-' : String(value);
                      return (
                        <td
                          key={c.id}
                          style={{ textAlign: c.align }}
                          className={extraCls}
                          title={text}
                        >
                          {text}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </SectionBox>
  );
};

export default ElecDetailTable;
